import React, {useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef} from 'react';
import md5 from 'js-md5';
import { useUserContext } from "../../contexts";
import { searchAssets, subscribeUserFn, meFn, Analytics} from '../../lib/api';
import useClickable from '../../hooks/useClickable';
import Menu from './Menu';
import Logo from './logo.svg';
import LogoName from './logo.name.svg';
import LogoIcon from './logo.icon.svg';
import { Spinner } from '../Spinner';
import Survey from '../Survey/Survey';
import './Banner.scss';
import CATEGORIES from './categories.json';
import { getCookie } from '../../lib/utils';
import configValues from '../../lib/config';


const BASE_API_MAP = {
    //'development': 'http://crate-core.com',
    'development': 'https://rest-qa.productioncrate.com',
    'qa': 'https://rest-qa.productioncrate.com',
    'production': 'https://rest.productioncrate.com',
    'webapp': 'https://rest.productioncrate.com'
}
const getEnvironment = () => {
    if (window.location.hostname==='app.productioncrate.com') {
        return 'webapp';
    }
    if ( window.location.hostname==='localhost') {
        return 'development';
    }
    if (window.location.hostname === "staging.productioncrate.com") {
        return "qa";
    }
    if (window.location.hostname === "user-qa.productioncrate.com") {
        return "qa";
    }
    return "production";
};
const env = getEnvironment();



const Banner = () => {

    const {user, userToken, unloadUser, focusMode, isAuthenticating, assetsAvailable} = useUserContext();
    const [banners, setBanners] = useState([]);
    const [bannerToDisplay, setBannerToDisplay] = useState(null);
    
    const baseUrl = process.env.REACT_APP_API_BASE_URL || BASE_API_MAP[env] || 'https://rest.productioncrate.com';

    // Legacy banner tag filtering is intentionally disabled.
    // Tags previously supported by this component:
    // - "cohort_a": show only to logged-in users with odd/even cohort (based on `user.id % 2`).
    // - "cohort_b": show only to logged-in users with the opposite cohort (based on `user.id % 2`).
    // - "old_user": show only to logged-in users created more than 12 months ago.
    // - "always_free": show only to logged-in users where `user.always_free` is true.
    // - "all_logged_in_users": exception tag to allow showing a banner to paid users (Pro) that would
    //   otherwise be filtered out.
    //
    // This logic was coupling product rules to the frontend and made banner behavior harder to reason
    // about. Banners are now displayed without applying tag-based gating in the frontend.
    const ENABLE_BANNER_TAG_FILTERS = false;

    const looksLikeHtml = (value) => {
        if (!value) return false;
        // Simple heuristic: treat strings containing tags as HTML.
        return /<\/?[a-z][\s\S]*>/i.test(String(value));
    };

    const escapeHtml = (value) => {
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    };

    // Minimal allowlist sanitizer for banner messages.
    // If you need richer HTML support, prefer a dedicated sanitizer library (e.g. DOMPurify).
    const sanitizeBannerHtml = (html) => {
        if (!html) return "";
        if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
            return escapeHtml(html);
        }

        const parser = new window.DOMParser();
        const doc = parser.parseFromString(String(html), "text/html");

        const allowedTags = new Set([
            "A",
            "B",
            "BR",
            "EM",
            "I",
            "LI",
            "OL",
            "P",
            "SMALL",
            "SPAN",
            "STRONG",
            "SUB",
            "SUP",
            "UL",
        ]);

        const isSafeHref = (href) => {
            if (!href) return false;
            const trimmed = String(href).trim();
            if (trimmed.startsWith("/")) return true;
            if (trimmed.startsWith("#")) return true;
            try {
                const url = new URL(trimmed, window.location.origin);
                return ["http:", "https:", "mailto:", "tel:"].includes(url.protocol);
            } catch (e) {
                return false;
            }
        };

        const sanitizeNode = (node) => {
            if (node.nodeType === window.Node.COMMENT_NODE) {
                node.remove();
                return;
            }

            if (node.nodeType === window.Node.TEXT_NODE) {
                return;
            }

            if (node.nodeType !== window.Node.ELEMENT_NODE) {
                node.remove();
                return;
            }

            const tagName = node.tagName;
            if (!allowedTags.has(tagName)) {
                // Replace disallowed elements with their text content (keeps the message readable).
                const text = doc.createTextNode(node.textContent || "");
                node.replaceWith(text);
                return;
            }

            // Strip all attributes except a small allowlist.
            const attrs = Array.from(node.attributes || []);
            for (const attr of attrs) {
                const name = attr.name.toLowerCase();
                const value = attr.value;

                // Remove inline event handlers and styles.
                if (name.startsWith("on") || name === "style") {
                    node.removeAttribute(attr.name);
                    continue;
                }

                if (tagName === "A") {
                    if (name === "href") {
                        if (!isSafeHref(value)) {
                            node.removeAttribute(attr.name);
                        }
                        continue;
                    }
                    if (name === "target" || name === "rel" || name === "title") {
                        continue;
                    }
                    node.removeAttribute(attr.name);
                    continue;
                }

                // For non-links, drop all attributes.
                node.removeAttribute(attr.name);
            }

            if (tagName === "A") {
                // Ensure external links don't get an opener reference.
                const target = node.getAttribute("target");
                if (target === "_blank") {
                    const rel = node.getAttribute("rel") || "";
                    const relParts = new Set(rel.split(/\s+/).filter(Boolean));
                    relParts.add("noopener");
                    relParts.add("noreferrer");
                    node.setAttribute("rel", Array.from(relParts).join(" "));
                }
            }

            const children = Array.from(node.childNodes || []);
            for (const child of children) sanitizeNode(child);
        };

        const bodyChildren = Array.from(doc.body.childNodes || []);
        for (const child of bodyChildren) sanitizeNode(child);

        return doc.body.innerHTML;
    };

    useEffect(() => {
        // Fetch banners with auth when a user/token exists; fall back to public banners on auth failure.
        // We wait until authentication is resolved to avoid sending stale/unknown auth state.
        if (isAuthenticating) return;

        // Prefer context token (covers localStorage/cookie) and fall back to cookie.
        const token = userToken || getCookie(configValues.sessionTokenCookieKey);
        const shouldUseAuth = !!user || !!token;
        const controller = new AbortController();

        const fetchBanners = async () => {
            const url = `${baseUrl}/web/banners`;

            const fetchOnce = async (withAuth) => {
                const headers = withAuth && token
                    ? { Authorization: `Bearer ${token}` }
                    : undefined;

                const response = await fetch(url, { headers, signal: controller.signal });
                if (!response.ok) {
                    const err = new Error(`Banners request failed (${response.status})`);
                    err.status = response.status;
                    throw err;
                }
                return response.json();
            };

            try {
                const data = shouldUseAuth ? await fetchOnce(true) : await fetchOnce(false);
                setBanners(data);
            } catch (e) {
                // If token is expired/invalid (or server rejects it), retry without auth.
                if (shouldUseAuth && (e?.status === 401 || e?.status === 403)) {
                    try {
                        const data = await fetchOnce(false);
                        setBanners(data);
                    } catch (e2) {
                        //console.log("Error fetching banners", url, e2);
                    }
                } else {
                    //console.log("Error fetching banners", url, e);
                }
            }
        };

        fetchBanners();
        return () => controller.abort();
    }, [baseUrl, isAuthenticating, user, userToken]);

    useEffect(() => {
        //console.log("BANNER EFFECTS");
        if(!banners || banners.length === 0){
            //console.log("no banner to show");
            return;
        }
        
        if(!user && isAuthenticating){
            //console.log("user is authenticating...")
            return;
        }
        //console.log("banner user:", user);
        let seenBanners = {};

        try {
            seenBanners = JSON.parse(user?.settings?.banners || '{}');
        } catch (e) {
            //console.log("Problem parsing banners", e);
            return;
        }

        let newBanners = banners.filter(banner => !seenBanners.hasOwnProperty(+banner.id));
        //console.log("newBanners", newBanners);

        //blacklist urls where we don't want to display banners
        const blacklist = ["register", "upgrade", "signup"];
        if(blacklist.some(substring => window.location.href.includes(substring))){
            //console.log("blacklist return");
            return;
        }

        if (ENABLE_BANNER_TAG_FILTERS) {
            newBanners = newBanners.filter(banner => {
                const tags = Array.isArray(banner.tags) ? banner.tags : [];

                if (!user && tags.some(tag => ["cohort_a", "cohort_b", "old_user"].includes(tag))) {
                    return false;
                }
                if (tags.includes("cohort_a") && user.id % 2 !== 0) {
                    return false;
                }
                if (tags.includes("cohort_b") && user.id % 2 === 0) {
                    return false;
                }
                if (tags.includes("old_user")) {
                    const createdAt = new Date(user.created_at);
                    const monthsAgo = new Date();
                    monthsAgo.setMonth(monthsAgo.getMonth() - 12);
                    if (createdAt >= monthsAgo) {
                        return false;
                    }
                }
                if (tags.includes("always_free") && !user.always_free) {
                    return false;
                }
                if (
                    user &&
                    user.team &&
                    user.team.subscription &&
                    user.team.subscription.plan.price > 0 &&
                    !tags.includes("all_logged_in_users")
                ) {
                    return false;
                }
                return true;
            });
        }

        if(newBanners.length === 0){
            return;
        }
        let bannerTemp = newBanners[0];
        if(bannerTemp.message && bannerTemp.message.includes("{{user}}")){
            const userName = printUserAName();
            // Ensure the user's name is treated as plain text when the banner message is HTML.
            const replacement = looksLikeHtml(bannerTemp.message) ? escapeHtml(userName) : userName;
            bannerTemp = { ...bannerTemp, message: bannerTemp.message.replace("{{user}}", replacement) };
        }

        setBannerToDisplay(bannerTemp);
        
    }, [user, isAuthenticating, banners]);
    useEffect(() => {
        if (!bannerToDisplay) {
            return;
        }
        Analytics.view('banner', {target: `banner_${bannerToDisplay.title.toLowerCase().replace(/ /g, "_")}`});
    }, [bannerToDisplay]);
    const hideBanner = (id) => {
        setBannerToDisplay(null);
        let seenBanners = {};
        try{
            seenBanners = user.settings && user.settings.banners?JSON.parse(user.settings.banners):{};
        }catch(e){
            //console.log("Problem parsing banners", e);
        }
        seenBanners[+id] = true;
        meFn({banners: JSON.stringify(seenBanners)});
        Analytics.sendCustomEvent(`close_banner`, {target: `${bannerToDisplay.title.toLowerCase().replace(/ /g, "_")}`});
    };

    const printUserAName = () => {
            
        if (user.first_name && user.first_name!=='no_firstname') {
            return user.first_name;
        }
        if(user.username === user.email){
            return "valued user";
        }

        return user.username;
    };

    const messageIsHtml = useMemo(() => {
        return looksLikeHtml(bannerToDisplay?.message);
    }, [bannerToDisplay?.message]);

    const sanitizedMessageHtml = useMemo(() => {
        if (!messageIsHtml || !bannerToDisplay?.message) return "";
        return sanitizeBannerHtml(bannerToDisplay.message);
    }, [bannerToDisplay?.message, messageIsHtml]);

    const handleBannerClick = (e) => {
        // If the user clicked a link inside the HTML message, let the link behave normally.
        const clickedLink = e?.target?.closest ? e.target.closest("a") : null;
        if (clickedLink) return;
        handleBannerNavigation();
    };

    const handleBannerNavigation = () => {
        if (!bannerToDisplay?.url) return;
        Analytics.click(`banner_${bannerToDisplay.title.toLowerCase().replace(/ /g, "_")}`).finally(() => {
            document.location.href = bannerToDisplay.url;
        });
    };

    const handleBannerKeyDown = (e) => {
        const focusedLink = e?.target?.closest ? e.target.closest("a") : null;
        if (focusedLink) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBannerNavigation();
        }
    };

    const handleBannerClose = (e) => {
        e.stopPropagation();
        hideBanner(bannerToDisplay.id);
    };

    const handleBannerCloseKeyDown = (e) => {
        e.stopPropagation();
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            hideBanner(bannerToDisplay.id);
        }
    };
    return (
        <>
            {bannerToDisplay &&
            <div
                className={`banner_web ${bannerToDisplay.title.toLowerCase().replace(/ /g, "_")} ${bannerToDisplay.url ? "banner_clickable" : ""} ${bannerToDisplay.image?.url ? "banner_with_image" : ""}`}
                onClick={bannerToDisplay.url ? handleBannerClick : undefined}
                onKeyDown={bannerToDisplay.url ? handleBannerKeyDown : undefined}
                role={bannerToDisplay.url ? "link" : undefined}
                tabIndex={bannerToDisplay.url ? 0 : undefined}
            >

                {bannerToDisplay.image && bannerToDisplay.image.url &&
                <img src={bannerToDisplay.image.url} className="banner_pic" alt="banner" />
                }
                {!bannerToDisplay.image &&
                <span className="banner_content">
                    {bannerToDisplay.url && 
                    <>
                        {messageIsHtml ? (
                            <div
                                className="banner_message banner_message_html"
                                dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
                            />
                        ) : (
                            <div
                                className="banner_message banner_message_plain"
                            >
                                {bannerToDisplay.message}
                            </div>
                        )}
                    </>
                    }
                    {!bannerToDisplay.url &&
                    <>
                        {messageIsHtml ? (
                            <span
                                className="banner_message banner_message_html"
                                dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
                            />
                        ) : (
                            <>
                                {bannerToDisplay.message}
                            </>
                        )}
                    </>
                    }
                </span>
                }
                <span
                    className="banner_content banner_close glyphicon glyphicon-cross"
                    onClick={handleBannerClose}
                    onKeyDown={handleBannerCloseKeyDown}
                    role="button"
                    tabIndex={0}
                    aria-label="Close banner"
                >
                </span>
            </div>
            }
        </>
    );
};

export default Banner;
